import { useMemo, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCorners,
    useSensor,
    useSensors,
} from '@dnd-kit/core';

import BoardColumn from './BoardColumn';
import resolveDrop from '../../../helpers/board/resolveDrop';

// Een bord met een kolom per status, waar je een kaart tussen kolommen sleept.
//
// Eén DndContext over álle kolommen — anders dan het TOC-overzicht, waar elke
// kolom er een eigen heeft juist om te voorkómen dat een kaart naar een andere
// kolom gaat. Hier is die overgang de hele bedoeling.
//
// AI-CONTEXT: See docs/features/household-board-view.md
// AI-CONTEXT: See docs/patterns.md#ui-status-board

/** 8px voordat een sleep begint, zodat een klik op de kaart een klik blijft. */
const DRAG_START_DISTANCE = 8;

/** Hoeveel kaarten een kolom in één keer toont. */
const DEFAULT_PAGE_SIZE = 25;

/**
 * @param {object} props
 * @param {Array<{key: string, label: string, items: Array<object>, count: number}>} props.columns - Uitkomst van `groupItemsByStatus`
 * @param {(item: object) => string|number} props.getItemKey - Sleutel per record
 * @param {(item: object) => React.ReactNode} props.renderCard - De inhoud van een kaart
 * @param {(move: {itemId: string|number, from: string, to: string}) => void} props.onMove - Aangeroepen bij een echte verplaatsing
 * @param {boolean} [props.canDrag] - Of er gesleept mag worden; zonder bewerkrecht uit
 * @param {number} [props.pageSize] - Hoeveel kaarten een kolom in één keer toont
 * @param {string} [props.emptyLabel] - Tekst in een lege kolom
 * @returns {JSX.Element}
 */
const StatusBoard = ({
    columns,
    getItemKey,
    renderCard,
    onMove,
    canDrag = true,
    pageSize = DEFAULT_PAGE_SIZE,
    emptyLabel,
}) => {
    const [sleepend, setSleepend] = useState(/** @type {object | null} */ (null));

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: DRAG_START_DISTANCE } }),
        useSensor(KeyboardSensor),
    );

    const kolomSleutels = useMemo(() => columns.map((column) => column.key), [columns]);

    /** @param {object} event - dnd-kit's onDragStart */
    const onDragStart = ({ active }) => {
        const item = columns
            .flatMap((column) => column.items)
            .find((kandidaat) => String(getItemKey(kandidaat)) === String(active.id));
        setSleepend(item || null);
    };

    /** @param {object} event - dnd-kit's onDragEnd */
    const onDragEnd = (event) => {
        setSleepend(null);
        // Alle 'doe niets'-gevallen zitten in resolveDrop, met tests.
        const move = resolveDrop(event, kolomSleutels);
        if (move) onMove(move);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={() => setSleepend(null)}
        >
            {/* Het bord scrolt horizontaal in zichzelf; de PAGINA mag niet
                meeschuiven, anders loopt de rest van het scherm weg. */}
            <div className="status-board">
                {columns.map((column) => (
                    <BoardColumn
                        key={column.key}
                        columnKey={column.key}
                        label={column.label}
                        items={column.items}
                        count={column.count}
                        pageSize={pageSize}
                        getItemKey={getItemKey}
                        renderCard={renderCard}
                        canDrag={canDrag}
                        emptyLabel={emptyLabel}
                    />
                ))}
            </div>

            {/* De kaart onder de muis wordt buiten de kolom getekend, zodat hij
                niet door de rand van zijn eigen kolom wordt afgeknipt. */}
            <DragOverlay>
                {sleepend ? (
                    <div className="board-card board-card--overlay">{renderCard(sleepend)}</div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default StatusBoard;
